import React, { useRef, useState } from 'react';
import { message, Button, Modal, Select, Typography, Tag as CustomTag } from 'antd';
import styled from 'styled-components';
import { SearchOutlined } from '@ant-design/icons';

import { useGetSearchResultsLazyQuery } from '../../../graphql/search.generated';
import { EntityType, Tag, Entity, ResourceRefInput } from '../../../types.generated';
import CreateTagModal from './CreateTagModal';
import {
    useBatchAddTagsMutation,
    useBatchAddTermsMutation,
    useBatchRemoveTagsMutation,
    useBatchRemoveTermsMutation,
    useBatchSetDomainMutation,
    useBatchUpdateTagsMutation,
    useBatchUpdateTermsMutation,
} from '../../../graphql/mutations.generated';
import { useEnterKeyListener } from '../useEnterKeyListener';
import GlossaryBrowser from '../../glossary/GlossaryBrowser/GlossaryBrowser';
import ClickOutside from '../ClickOutside';
import { useEntityRegistry } from '../../useEntityRegistry';
import { useGetRecommendations } from '../recommendation';
import { FORBIDDEN_URN_CHARS_REGEX, handleBatchError } from '../../entity/shared/utils';
import { TagTermLabel } from './TagTermLabel';
import { ENTER_KEY_CODE } from '../constants';
import { DefaultValueType } from './DefaultValueType';

export enum OperationType {
    ADD,
    REMOVE,
    EDIT,
}

type EditTagsModalProps = {
    visible: boolean;
    onCloseModal: () => void;
    resources: ResourceRefInput[];
    type?: EntityType;
    operationType?: OperationType;
    defaultValues?: DefaultValueType[];
    onOkOverride?: (result: string[]) => void;
    onSuccess?: () => void;
    refetch?: () => void;
};

const TagSelect = styled(Select)`
    width: 100%;

    &.ant-select-multiple.ant-select-show-arrow {
        .ant-select-selector {
            padding-right: 32px;
        }
    }
`;

const CustomChip = styled(CustomTag)`
    > div {
        &:before {
            display: none;
        }
    }
`;

export const BrowserWrapper = styled.div<{ isHidden: boolean; width?: string; maxHeight?: number; tabIndex?: number }>`
    background-color: white;
    border-radius: 5px;
    box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%), 0 9px 28px 8px rgb(0 0 0 / 5%);
    max-height: ${(props) => (props.maxHeight ? props.maxHeight : '380')}px;
    overflow: auto;
    position: absolute;
    transition: opacity 0.2s;
    width: ${(props) => (props.width ? props.width : 'calc(100% - 80px)')};
    z-index: 1051;
    ${(props) =>
        props.isHidden &&
        `
        opacity: 0;
        height: 0;
    `}
`;

const CREATE_TAG_VALUE = '____reserved____.createTagValue';

const isValidTagName = (tagName: string) => {
    return tagName && tagName.length > 0 && !FORBIDDEN_URN_CHARS_REGEX.test(tagName);
};

const defaultValuesToSelectedValue = (defaultValues?: DefaultValueType[]): any[] => {
    return (
        defaultValues?.map((defaultValue: DefaultValueType) => ({
            urn: defaultValue.urn,
            component: <TagTermLabel entity={defaultValue.entity} />,
        })) || []
    );
};

export default function EditTagTermsModal({
    visible,
    onCloseModal,
    resources,
    type = EntityType.Tag,
    operationType = OperationType.ADD,
    defaultValues = [],
    onOkOverride,
    onSuccess,
    refetch,
}: EditTagsModalProps) {
    const entityRegistry = useEntityRegistry();
    const [inputValue, setInputValue] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [disableAction, setDisableAction] = useState(false);
    const [urns, setUrns] = useState<string[]>(defaultValues.map((defaultValue) => defaultValue.urn));
    const [selectedTerms, setSelectedTerms] = useState<any[]>(defaultValuesToSelectedValue(defaultValues));

    const [selectedTags, setSelectedTags] = useState<any[]>(defaultValuesToSelectedValue(defaultValues));

    const [entityToRemove, setEntityToRemove] = useState<string[]>([]);

    const [defaultValueEntity, setDefaultValueEntity] = useState<any[]>(
        operationType !== OperationType.EDIT ? [] : defaultValuesToSelectedValue(defaultValues),
    );

    const [selectedDomain, setSelectedDomain] = useState<any[]>(
        type === EntityType.Domain ? defaultValuesToSelectedValue(defaultValues) : [],
    );

    const [isFocusedOnInput, setIsFocusedOnInput] = useState(false);

    const [batchAddTagsMutation] = useBatchAddTagsMutation();
    const [batchRemoveTagsMutation] = useBatchRemoveTagsMutation();
    const [batchAddTermsMutation] = useBatchAddTermsMutation();
    const [batchRemoveTermsMutation] = useBatchRemoveTermsMutation();
    const [batchSetDomain] = useBatchSetDomainMutation();
    const [batchUpdateTagsMutation] = useBatchUpdateTagsMutation();
    const [batchUpdateTermsMutation] = useBatchUpdateTermsMutation();

    const [tagTermSearch, { data: tagTermSearchData }] = useGetSearchResultsLazyQuery();
    const tagSearchResults = tagTermSearchData?.search?.searchResults?.map((searchResult) => searchResult.entity) || [];
    const [recommendedData] = useGetRecommendations([type]);
    const [itemsToRender, setItemsToRender] = useState<any[]>(defaultValuesToSelectedValue(defaultValues));
    const inputEl = useRef(null);

    const handleSearch = (text: string) => {
        if (text.length > 0) {
            tagTermSearch({
                variables: {
                    input: {
                        type,
                        query: text,
                        start: 0,
                        count: 10,
                    },
                },
            });
        }
    };

    const renderSearchResult = (entity: Entity) => {
        const displayName =
            entity.type === EntityType.Tag ? (entity as Tag).name : entityRegistry.getDisplayName(entity.type, entity);
        const tagOrTermComponent = <TagTermLabel entity={entity} />;
        return (
            <Select.Option
                data-testid="tag-term-option"
                value={entity.urn}
                key={entity.urn}
                name={displayName}
                tabIndex={0}
            >
                {tagOrTermComponent}
            </Select.Option>
        );
    };

    const tagResult =
        (!inputValue || inputValue.length === 0) && type !== EntityType.GlossaryTerm
            ? recommendedData
            : tagSearchResults;

    const tagSearchOptions = tagResult?.map((result) => {
        return renderSearchResult(result);
    });

    const inputExistsInTagSearch = tagSearchResults.some((entity: Entity) => {
        const displayName = entityRegistry.getDisplayName(entity.type, entity);
        return displayName.toLowerCase() === inputValue.toLowerCase();
    });

    if (
        operationType === OperationType.ADD &&
        !inputExistsInTagSearch &&
        isValidTagName(inputValue) &&
        type === EntityType.Tag &&
        urns.length === 0
    ) {
        tagSearchOptions?.push(
            <Select.Option value={CREATE_TAG_VALUE} key={CREATE_TAG_VALUE}>
                <Typography.Link> Create {inputValue}</Typography.Link>
            </Select.Option>,
        );
    }

    const chipRender = ({ selectedItem, onClose }) => {
        const onPreventMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };

        return (
            <CustomChip onMouseDown={onPreventMouseDown} closable onClose={onClose} className="f-outlined base-tag">
                {selectedItem}
            </CustomChip>
        );
    };

    // Handle the Enter press
    useEnterKeyListener({
        querySelectorToExecuteClick: '#addTagButton',
    });

    function handleOnClickBack() {
        setInputValue('');
        setShowCreateModal(false);
    }

    if (showCreateModal) {
        return (
            <CreateTagModal
                visible={visible}
                onClose={onCloseModal}
                onBack={handleOnClickBack}
                tagName={inputValue}
                resources={resources}
            />
        );
    }

    // When a Tag or term search result is selected, add the urn to the Urns
    const onSelectValue = (urn: string) => {
        if (urn === CREATE_TAG_VALUE) {
            if (isValidTagName(inputValue)) {
                setShowCreateModal(true);
            }
            return;
        }
        const newUrns = type === EntityType.Domain ? [urn] : [...(urns || []), urn];
        const selectedSearchOption = tagSearchOptions?.find((option) => option.props.value === urn);
        const selectedTagOption = tagResult?.find((tag) => tag.urn === urn);
        const selectedDomainOption = tagResult?.find((domain) => domain.urn === urn);
        setUrns(newUrns);
        setSelectedTerms([
            ...selectedTerms,
            { urn, component: <TagTermLabel termName={selectedSearchOption?.props.name} /> },
        ]);
        setSelectedTags([
            ...selectedTags,
            {
                urn,
                component: <TagTermLabel entity={selectedTagOption} />,
            },
        ]);
        setSelectedDomain([{ urn, component: <TagTermLabel entity={selectedDomainOption} /> }]);
        if (!itemsToRender.some((item) => item.urn === urn)) {
            const component =
                type === EntityType.GlossaryTerm ? (
                    <TagTermLabel termName={selectedSearchOption?.props.name} />
                ) : (
                    <TagTermLabel entity={selectedTagOption} />
                );
            setItemsToRender([
                ...itemsToRender,
                {
                    urn,
                    component,
                },
            ]);
        }

        if (inputEl && inputEl.current) {
            (inputEl.current as any).blur();
        }
        setInputValue('');
    };

    // When a Tag or term search result is deselected, remove the urn from the Owners
    const onDeselectValue = (urn: string) => {
        const newUrns = urns?.filter((u) => u !== urn);
        setUrns(newUrns);
        setInputValue('');
        // Only set focus on input for TagSelect, not for GlossaryTerm
        if (type !== EntityType.GlossaryTerm) {
            setIsFocusedOnInput(true);
        }
        setSelectedTerms(selectedTerms.filter((term) => term.urn !== urn));
        setSelectedTags(selectedTags.filter((term) => term.urn !== urn));
        setSelectedDomain(selectedDomain.filter((domain) => domain.urn !== urn));
        if (defaultValueEntity.some((item) => item.urn === urn)) {
            setEntityToRemove([...entityToRemove, urn]);
        }
        setDefaultValueEntity(defaultValueEntity.filter((tag) => tag.urn !== urn));
        setItemsToRender(itemsToRender.filter((item) => item.urn !== urn));
    };

    const getEntityName = (entityType: string) => {
        if (entityType === EntityType.GlossaryTerm) {
            return 'Terms';
        }
        if (entityType === EntityType.Domain) {
            return 'Domain';
        }
        return 'Tags';
    };

    const batchAddTags = () => {
        batchAddTagsMutation({
            variables: {
                input: {
                    tagUrns: urns,
                    resources,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: `Added ${getEntityName(type)}!`,
                        duration: 2,
                    });
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, { content: `Failed to add: \n ${e.message || ''}`, duration: 3 }),
                );
            })
            .finally(() => {
                setDisableAction(false);
                onCloseModal();
                refetch?.();
                setUrns([]);
            });
    };

    const batchAddTerms = () => {
        batchAddTermsMutation({
            variables: {
                input: {
                    termUrns: urns,
                    resources,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: `Added ${getEntityName(type)}!`,
                        duration: 2,
                    });
                    onSuccess?.();
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, { content: `Failed to add: \n ${e.message || ''}`, duration: 3 }),
                );
            })
            .finally(() => {
                setDisableAction(false);
                onCloseModal();
                refetch?.();
                setUrns([]);
            });
    };

    const batchRemoveTags = () => {
        batchRemoveTagsMutation({
            variables: {
                input: {
                    tagUrns: urns,
                    resources,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: `Removed ${getEntityName(type)}!`,
                        duration: 2,
                    });
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, { content: `Failed to remove: \n ${e.message || ''}`, duration: 3 }),
                );
            })
            .finally(() => {
                setDisableAction(false);
                onCloseModal();
                refetch?.();
                setUrns([]);
            });
    };

    const batchRemoveTerms = () => {
        batchRemoveTermsMutation({
            variables: {
                input: {
                    termUrns: urns,
                    resources,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: `Removed ${getEntityName(type)}!`,
                        duration: 2,
                    });
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, { content: `Failed to remove: \n ${e.message || ''}`, duration: 3 }),
                );
            })
            .finally(() => {
                setDisableAction(false);
                onCloseModal();
                refetch?.();
                setUrns([]);
            });
    };

    const batchAddDomain = () => {
        batchSetDomain({
            variables: {
                input: {
                    domainUrn: urns[0],
                    resources,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: `Added ${getEntityName(type)}!`,
                        duration: 2,
                    });
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, { content: `Failed to add: \n ${e.message || ''}`, duration: 3 }),
                );
            })
            .finally(() => {
                setDisableAction(false);
                onCloseModal();
                refetch?.();
                setUrns([]);
            });
    };

    const batchRemoveDomain = () => {
        batchSetDomain({
            variables: {
                input: {
                    resources,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: `Removed ${getEntityName(type)}!`,
                        duration: 2,
                    });
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, { content: `Failed to remove: \n ${e.message || ''}`, duration: 3 }),
                );
            })
            .finally(() => {
                setDisableAction(false);
                onCloseModal();
                refetch?.();
                setUrns([]);
            });
    };

    const batchUpdateTags = (tagsToRemove: string[], tagsToAdd: string[]) => {
        batchUpdateTagsMutation({
            variables: {
                input: {
                    tagsToRemove,
                    tagsToAdd,
                    resources,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: `Updated ${getEntityName(type)}!`,
                        duration: 2,
                    });
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, { content: `Failed to update: \n ${e.message || ''}`, duration: 3 }),
                );
            })
            .finally(() => {
                setDisableAction(false);
                onCloseModal();
                refetch?.();
                setUrns([]);
            });
    };

    const batchUpdateTerms = (termsToRemove: string[], termsToAdd: string[]) => {
        batchUpdateTermsMutation({
            variables: {
                input: {
                    termsToRemove,
                    termsToAdd,
                    resources,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({
                        content: `Updated ${getEntityName(type)}!`,
                        duration: 2,
                    });
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, { content: `Failed to update: \n ${e.message || ''}`, duration: 3 }),
                );
            })
            .finally(() => {
                setDisableAction(false);
                onCloseModal();
                refetch?.();
                setUrns([]);
            });
    };

    const editTags = async () => {
        if (operationType === OperationType.ADD) {
            batchAddTags();
        } else if (operationType === OperationType.EDIT) {
            const entityToAdd = urns.filter(
                (urn) =>
                    !defaultValues.some((defaultValue) => defaultValue.urn === urn) && !entityToRemove.includes(urn),
            );
            batchUpdateTags(entityToRemove, entityToAdd);
        } else {
            batchRemoveTags();
        }
    };

    const editTerms = () => {
        if (operationType === OperationType.ADD) {
            batchAddTerms();
        } else if (operationType === OperationType.EDIT) {
            const entityToAdd = urns.filter(
                (urn) =>
                    !defaultValues.some((defaultValue) => defaultValue.urn === urn) && !entityToRemove.includes(urn),
            );
            batchUpdateTerms(entityToRemove, entityToAdd);
        } else {
            batchRemoveTerms();
        }
    };

    const editDomain = () => {
        if (operationType === OperationType.ADD) {
            batchAddDomain();
        } else {
            batchRemoveDomain();
        }
    };

    // Function to handle the modal action's
    const onOk = () => {
        if (onOkOverride) {
            onOkOverride(urns);
            return;
        }

        if (!resources) {
            onCloseModal();
            return;
        }
        setDisableAction(true);

        if (type === EntityType.Tag) {
            editTags();
        } else if (type === EntityType.Domain) {
            editDomain();
        } else {
            editTerms();
        }
    };

    function selectTermFromBrowser(urn: string, displayName: string) {
        setIsFocusedOnInput(false);
        const newUrns = [...(urns || []), urn];
        setUrns(newUrns);
        setSelectedTerms([...selectedTerms, { urn, component: <TagTermLabel termName={displayName} /> }]);
        if (!itemsToRender.some((item) => item.urn === urn)) {
            setItemsToRender([
                ...itemsToRender,
                {
                    urn,
                    component: <TagTermLabel termName={displayName} />,
                },
            ]);
        }
    }

    function clearInput() {
        setInputValue('');
        setTimeout(() => setIsFocusedOnInput(true), 0); // call after click outside
    }

    function handleBlur() {
        setInputValue('');
    }

    function handleKeyDown(event) {
        if (event.keyCode === ENTER_KEY_CODE) {
            (inputEl.current as any).blur();
        }
    }

    const isShowingGlossaryBrowser = !inputValue && type === EntityType.GlossaryTerm && isFocusedOnInput;

    const defaultTitle =
        {
            [OperationType.ADD]: 'Add',
            [OperationType.EDIT]: 'Edit',
        }[operationType] || 'Remove';

    return (
        <Modal
            centered
            visible={visible}
            className="f-modal"
            onCancel={onCloseModal}
            title={
                <h1>
                    {defaultTitle} {entityRegistry.getEntityName(type)}s
                </h1>
            }
            footer={
                <>
                    <Button onClick={onCloseModal} type="default" className="ant-btn-default">
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        id="addTagButton"
                        data-testid="add-tag-term-from-modal-btn"
                        onClick={onOk}
                        disabled={(urns.length === 0 && entityToRemove.length === 0) || disableAction}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <ClickOutside onClickOutside={() => setIsFocusedOnInput(false)}>
                <TagSelect
                    mode="multiple"
                    autoClearSearchValue
                    showArrow
                    autoFocus
                    showSearch
                    size="large"
                    searchValue={inputValue}
                    value={[]}
                    ref={inputEl}
                    filterOption={false}
                    defaultActiveFirstOption={false}
                    data-testid="tag-term-modal-input"
                    onSelect={(asset: any) => onSelectValue(asset)}
                    onDeselect={(asset: any) => onDeselectValue(asset)}
                    placeholder={
                        <>
                            <SearchOutlined className="f-color-dark-black-s80" /> Search for{' '}
                            {entityRegistry.getEntityName(type)?.toLowerCase()}s...
                        </>
                    }
                    onSearch={(value: string) => {
                        // eslint-disable-next-line react/prop-types
                        handleSearch(value);
                        // eslint-disable-next-line react/prop-types
                        setInputValue(value);
                    }}
                    onClear={clearInput}
                    onFocus={() => setIsFocusedOnInput(true)}
                    onBlur={handleBlur}
                    onInputKeyDown={handleKeyDown}
                    aria-label={`Choose ${entityRegistry.getEntityName(type)?.toLowerCase()}`}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    dropdownStyle={isShowingGlossaryBrowser ? { display: 'none' } : {}}
                >
                    {tagSearchOptions}
                </TagSelect>
                <BrowserWrapper isHidden={!isShowingGlossaryBrowser}>
                    <GlossaryBrowser isSelecting selectTerm={selectTermFromBrowser} />
                </BrowserWrapper>
                <div className="f-tag-container mt-2">
                    {itemsToRender.map((item) => {
                        return chipRender({
                            selectedItem: item.component,
                            onClose: () => {
                                onDeselectValue(item.urn);
                            },
                        });
                    })}
                </div>
            </ClickOutside>
        </Modal>
    );
}
